/**
 * Farm-profile option catalogs — the canonical strings a FarmProfile can hold.
 *
 * Ported verbatim from the ScenarioA prototype. Source: Jordbruksverkets
 * typologi (SAM / CDB / JEU / FADN).
 *
 * Lives in shared/ so the onboarding UI, the admin filter editor and the shared
 * profile-warning rules all read one definition. The frontend re-exports this
 * file from `@/constants/farm-profile/options`.
 */

export interface ProdGroup {
  group: string;
  items: string[];
}

/** Group name for the animal section. Used by StepManualProduction to
 *  render the "non-food exception" sub-selector. */
export const ANIMAL_GROUP_NAME = 'Djur' as const;

/** Produktionsgrenar — grouped for MultiChipSelect.
 *  Växtodling first (most common). Hästar moved into the animal group
 *  (they ARE animals). Annat merged into Övrig verksamhet, which is also the
 *  only home for gårdsbutik, besöksverksamhet and livsmedelsförädling: the
 *  separate tilläggsverksamhet list was removed in AG-274 because the wizard
 *  never asked it and admins filtered on the wrong field. */
export const PROD_OPTIONS: ProdGroup[] = [
  {
    group: 'Växtodling & vall',
    items: [
      'Spannmålsodling',
      'Vallproduktion',
      'Bete',
      'Sockerbetor',
      'Grönsaker',
      'Växthus',
    ],
  },
  {
    group: ANIMAL_GROUP_NAME,
    items: [
      'Mjölkproduktion',
      'Nötköttsproduktion',
      'Dikor',
      'Gris',
      'Får och/eller getter',
      'Slaktkyckling',
      'Unghönsuppfödning',
      'Ägg',
      'Hästar',
    ],
  },
  {
    group: 'Övrig verksamhet',
    items: [
      'Besöksverksamhet',
      'Gårdsbutik',
      'Egen livsmedelsförädling',
      'Energiproduktion',
    ],
  },
];

/** All animal items — derived once for use in filters / summaries. */
export const ANIMAL_ITEMS: string[] =
  PROD_OPTIONS.find((g) => g.group === ANIMAL_GROUP_NAME)?.items ?? [];

/**
 * Short, farmer-facing display labels for verbose production-type VALUES.
 * The canonical strings in PROD_OPTIONS are deliberately NOT renamed — they
 * stay stored in FarmProfile.productionTypes and matched by admin filter
 * rules, where the fuller "...produktion" form reads more precisely. This
 * map only changes what the farmer SEES in onboarding / the profile.
 */
export const PROD_DISPLAY_LABELS: Record<string, string> = {
  Mjölkproduktion: 'Mjölk',
  Nötköttsproduktion: 'Nötkött',
  Vallproduktion: 'Vall',
  Spannmålsodling: 'Spannmål',
  'Får och/eller getter': 'Får/getter',
  Unghönsuppfödning: 'Unghöns',
};

/** Short farmer-facing label for a production-type value (falls back to the
 *  canonical value when no shorter form is defined). */
export const prodLabel = (value: string): string =>
  PROD_DISPLAY_LABELS[value] ?? value;

/**
 * Animal types where "not for food" (hobby, sport, sällskap) is a realistic
 * exception worth asking about. The commercially-framed food grenar
 * (Mjölkproduktion, Nötköttsproduktion, Slaktkyckling, Unghönsuppfödning,
 * Ägg) are inherently food — offering to mark them non-food is
 * contradictory, so they're left out of the exception prompt. Must use the
 * exact ANIMAL_ITEMS strings.
 */
export const NON_FOOD_ELIGIBLE_ANIMALS: string[] = [
  'Dikor',
  'Gris',
  'Får och/eller getter',
  'Hästar',
];

/**
 * Naturbetesmark is asked on the mark (areal) page, not among the geographic
 * protection classes, but it is STORED in FarmProfile.geographicFactors so
 * existing profiles and admin filter rules keep working unchanged.
 */
export const NATURBETESMARK = 'Naturbetesmark' as const;

/** Geografiska skyddsklasser — the options shown on the geography step. */
export const GEO_OPTIONS: string[] = [
  'Nitratkänsligt område',
  'Vattenskyddsområde',
  'Natura 2000',
  'Naturreservat',
];

/** Every value geographicFactors can hold — what the admin filter editor offers. */
export const GEO_FILTER_OPTIONS: string[] = [...GEO_OPTIONS, NATURBETESMARK];

/**
 * IP-standarden (Sigill Kvalitetssystem). The umbrella value is the legacy
 * string 'IP Sigill' so profiles and filter rules written before the levels
 * existed stay valid; the farmer sees it as "IP-standarden (Sigill)".
 *
 * Level, tillval and inriktning are stored as separate 'IP …' strings in
 * FarmProfile.certifications alongside the umbrella, so an admin can filter
 * on e.g. "IP Nöt & Mjölk" or "IP Klimatcertifierad" directly.
 */
export const IP_STANDARD = 'IP Sigill' as const;

/** Certifieringsnivå — mutually exclusive. */
export const IP_LEVEL_OPTIONS: string[] = ['IP Grundcertifiering', 'IP Sigillnivå'];

/** Tillval on top of the level. */
export const IP_ADDON_OPTIONS: string[] = [
  'IP Klimatcertifierad',
  'IP Naturbeteskött',
  'IP Arbetsvillkor',
];

/**
 * Inriktning per produktion. Sigill certifies cattle and dairy as ONE scope,
 * "IP Nöt & Mjölk" (confirmed by Tove Bratt, AG-265). The catalog offered them
 * as two separate values until migration 20260916100000 merged stored data.
 */
export const IP_SCOPE_OPTIONS: string[] = [
  'IP Livsmedel',
  'IP Nöt & Mjölk',
  'IP Gris',
  'IP Kyckling',
  'IP Frukt & Grönt',
  'IP Spannmål & Oljeväxter',
  'IP Prydnadsväxter & Plantskola',
];

/** Every IP sub-value — pruned from certifications when the umbrella is deselected. */
export const IP_SUB_OPTIONS: string[] = [
  ...IP_LEVEL_OPTIONS,
  ...IP_ADDON_OPTIONS,
  ...IP_SCOPE_OPTIONS,
];

/** KRAV certifies on top of EU-ekologisk, so a KRAV farm always holds both. */
export const KRAV = 'KRAV' as const;
export const EU_ORGANIC = 'EU-ekologisk' as const;

/** Certifieringar — the top-level chips on the certification step. */
export const CERT_OPTIONS: string[] = [KRAV, EU_ORGANIC, IP_STANDARD, 'Global G.A.P'];

/** Every value certifications can hold — what the admin filter editor offers. */
export const CERT_FILTER_OPTIONS: string[] = [...CERT_OPTIONS, ...IP_SUB_OPTIONS];

/**
 * Farmer-facing labels for certification VALUES ('IP Sigill' → "IP-standarden
 * (Sigill)"). Defined in shared/farm-filter so the hide-reason texts use the
 * same names as the profile UI; re-exported here next to the other catalogs.
 */
import { certLabel } from '../farm-filter/labels';
export { CERT_DISPLAY_LABELS, certLabel } from '../farm-filter/labels';

/**
 * Label for an IP sub-value inside the IP panel, where the "IP " prefix is
 * already given by the panel heading: 'IP Nöt & Mjölk' → 'Nöt & Mjölk'.
 */
export const ipSubLabel = (value: string): string =>
  certLabel(value).replace(/^IP /, '');

/**
 * Anslutningar/kontrollprogram — memberships, not certifications.
 * Kokontrollen (Växa Sverige) and Gård & Djurhälsan's programmes.
 * Salmonellaprogrammet for nöt and gris was replaced by Smittsäkrad besättning
 * in 2015–2016 and was removed in AG-274 (migration 20260916130000).
 */
export const CONTROL_PROGRAM_OPTIONS: string[] = [
  'Kokontrollen',
  'Smittsäkrad besättning',
  'ViLA',
];

/** Övriga anmälningspliktiga verksamheter. Gårdsslakteri needs approval from
 *  Livsmedelsverket rather than a plain anmälan, but it belongs with the
 *  other activities that trigger authority contact. */
export const NOTIFIABLE_OPTIONS: string[] = [
  'Gårdspanna',
  'Biogas',
  'Gårdsslakteri',
  'Annan anmälningspliktig verksamhet',
];

/**
 * Ekostatus — kanonisk vokabulär. Dessa exakta strängar lagras i
 * FarmProfile.ecoStatus. Filterregler jämför med `eq` (ett värde) eller med
 * mängdoperatorerna `includesAny`/`excludesAny` (en lista, AG-275), så
 * filtereditorn MÅSTE erbjuda samma värden som profil-UI:t skriver.
 */
export const ECO_STATUS_OPTIONS: string[] = [
  'Konventionell',
  'Ekologisk',
  'Under omställning till ekologisk',
  // Parallellproduktion. `eq 'Ekologisk'` matchar bara exakt det värdet. "All
  // ekologisk produktion" skrivs som `excludesAny ['Konventionell']`, som även
  // fångar framtida värden, eller `includesAny` med de tre ekologiska värdena.
  'Både konventionell och ekologisk',
];
